import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { SaveForLaterStore } from '@theme/save-for-later';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #uncheckedKeys = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#bindSaveForLaterButtons();

    this.addEventListener('change', this.#onSelectionChange);
    this.addEventListener('click', this.#onBulkSelectionToggle);
    document.addEventListener('click', this.#onCheckoutClick, true);
    this.#applySelectionState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.querySelectorAll('[data-variant-id][data-product-handle]').forEach((btn) => {
      if (btn.classList.contains('cart-items__save-later')) {
        btn.removeEventListener('click', this.#onSaveForLater);
      }
    });

    this.removeEventListener('change', this.#onSelectionChange);
    this.removeEventListener('click', this.#onBulkSelectionToggle);
    document.removeEventListener('click', this.#onCheckoutClick, true);
  }

  /**
   * Re-applies the unchecked-state set to checkboxes and visual cues after re-renders.
   */
  #applySelectionState = () => {
    const rows = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.cart-items__table-row[data-key]')
    );
    for (const row of rows) {
      const { key, parentKey } = row.dataset;
      const effectiveKey = parentKey || key;
      const isUnchecked = this.#uncheckedKeys.has(effectiveKey);

      row.classList.toggle('cart-items__table-row--unselected', isUnchecked);

      const checkbox = row.querySelector('.cart-items__select');
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = !isUnchecked;
      }
    }

    this.#updateBulkSelectionToggle();
    this.#updateCheckoutButtonState();
  };

  /**
   * Updates bulk select toggle state and label based on current selection.
   */
  #updateBulkSelectionToggle = () => {
    const toggle = this.querySelector('[data-cart-select-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    const topLevelKeys = this.#getTopLevelKeys();
    if (topLevelKeys.length === 0) {
      toggle.hidden = true;
      return;
    }

    const selectedCount = topLevelKeys.filter((key) => !this.#uncheckedKeys.has(key)).length;
    const allSelected = selectedCount === topLevelKeys.length;
    if (allSelected) {
      toggle.dataset.action = 'unselect';
      toggle.textContent = toggle.dataset.unselectLabel || 'Unselect all';
      toggle.hidden = false;
      return;
    }
    toggle.dataset.action = 'select';
    toggle.textContent = toggle.dataset.selectLabel || 'Select all';
    toggle.hidden = false;
  };

  /**
   * Handles bulk selection button click.
   * @param {Event} event
   */
  #onBulkSelectionToggle = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-cart-select-toggle]');
    if (!(toggle instanceof HTMLButtonElement) || !this.contains(toggle)) return;

    const topLevelKeys = this.#getTopLevelKeys();
    if (topLevelKeys.length === 0) return;

    const selectedCount = topLevelKeys.filter((key) => !this.#uncheckedKeys.has(key)).length;
    const inferredAction = selectedCount === topLevelKeys.length ? 'unselect' : 'select';
    const action = toggle.dataset.action || inferredAction;

    if (action === 'unselect') {
      topLevelKeys.forEach((key) => this.#uncheckedKeys.add(key));
    } else if (action === 'select') {
      topLevelKeys.forEach((key) => this.#uncheckedKeys.delete(key));
    }

    this.#applySelectionState();
  };

  /**
   * Returns all top-level cart line keys.
   * @returns {string[]}
   */
  #getTopLevelKeys() {
    return Array.from(
      /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cart-items__table-row[data-key]'))
    )
      .filter((row) => !row.dataset.parentKey)
      .map((row) => row.dataset.key || '')
      .filter((key) => key.length > 0);
  }

  /**
   * Handles checkbox toggle on any cart row.
   * @param {Event} event
   */
  #onSelectionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.classList.contains('cart-items__select')) return;

    const lineKey = target.dataset.lineKey;
    if (!lineKey) return;

    if (target.checked) {
      this.#uncheckedKeys.delete(lineKey);
    } else {
      this.#uncheckedKeys.add(lineKey);
    }

    this.#applySelectionState();
  };

  /**
   * Updates the checkout button's disabled state based on current selection.
   */
  #updateCheckoutButtonState() {
    const myHost = this.closest('cart-drawer-component, .section');
    const checkoutButton = myHost?.querySelector('#checkout');
    if (!(checkoutButton instanceof HTMLButtonElement)) return;

    const topLevelKeys = this.#getTopLevelKeys();

    if (topLevelKeys.length === 0) return;

    const noneSelected = topLevelKeys.every((key) => this.#uncheckedKeys.has(key));
    checkoutButton.disabled = noneSelected;
  }

  /**
   * Intercepts the Checkout button click. If a partial selection is active:
   *   1. Snapshot unselected items (variant_id + quantity + properties + selling_plan_id)
   *      and the current cart token to localStorage so they can be restored later.
   *   2. Remove unselected items from the cart via /cart/update.js so checkout only
   *      contains the items the user wants to purchase.
   *   3. Redirect to /checkout.
   *
   * After the order completes (cart token rotates) — or if the user abandons and
   * returns — assets/cart-restore.js re-adds the snapshotted unselected items.
   *
   * @param {MouseEvent} event
   */
  #onCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const checkoutBtn = target.closest('#checkout');
    if (!checkoutBtn) return;

    const myHost = this.closest('cart-drawer-component, .section');
    const btnHost = checkoutBtn.closest('cart-drawer-component, .section');
    if (myHost !== btnHost) return;

    if (this.#uncheckedKeys.size === 0) return;

    const allRows = Array.from(
      /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cart-items__table-row[data-key]'))
    );
    const selectedTopRows = allRows.filter((row) => {
      const { key, parentKey } = row.dataset;
      if (parentKey) return false;
      return !this.#uncheckedKeys.has(key);
    });

    if (selectedTopRows.length === 0) {
      event.preventDefault();
      return;
    }

    const uncheckedKeysSnapshot = new Set(this.#uncheckedKeys);

    event.preventDefault();
    event.stopPropagation();

    this.#startPartialCheckout(uncheckedKeysSnapshot, checkoutBtn).catch(async (error) => {
      console.error('Cart partial checkout failed; falling back to full-cart checkout.', error);
      await window.BirchAI?.attribution?.prepareCheckout?.();
      window.location.href = '/checkout';
    });
  };

  /**
   * Snapshots unselected items to localStorage, removes them from the cart, then
   * redirects to /checkout.
   *
   * @param {Set<string>} uncheckedKeys - Snapshot of unchecked line keys at click time.
   * @param {Element} checkoutBtn - The checkout button (used to set a busy state).
   */
  async #startPartialCheckout(uncheckedKeys, checkoutBtn) {
    if (checkoutBtn instanceof HTMLButtonElement) checkoutBtn.disabled = true;
    this.#disableCartItems();

    const cartUrl = (Theme?.routes?.cart_url || '/cart') + '.js';
    const cartUpdateUrl = Theme?.routes?.cart_update_url || '/cart/update';

    const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
    /** @type {{ token: string, items: Array<any> }} */
    const cart = await cartResponse.json();

    const itemsToRestore = cart.items
      .filter((item) => uncheckedKeys.has(item.key) && !item.parent_relationship?.parent)
      .map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties && Object.keys(item.properties).length ? item.properties : undefined,
        selling_plan: item.selling_plan_allocation?.selling_plan?.id,
      }));

    if (itemsToRestore.length === 0) {
      await window.BirchAI?.attribution?.prepareCheckout?.();
      window.location.href = '/checkout';
      return;
    }

    try {
      localStorage.setItem(
        'birch:cart:pendingRestore',
        JSON.stringify({ token: cart.token, items: itemsToRestore, savedAt: Date.now() })
      );
    } catch (error) {
      console.warn('Cart partial checkout: localStorage unavailable, skipping restore snapshot.', error);
      await window.BirchAI?.attribution?.prepareCheckout?.();
      window.location.href = '/checkout';
      return;
    }

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of uncheckedKeys) updates[key] = 0;

    const updateResponse = await fetch(cartUpdateUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });

    if (!updateResponse.ok) {
      localStorage.removeItem('birch:cart:pendingRestore');
      throw new Error(`Failed to update cart: ${updateResponse.status}`);
    }

    await window.BirchAI?.attribution?.prepareCheckout?.();
      window.location.href = '/checkout';
  }

  /**
   * The row standing for one cart line, by its `cart.items` line number.
   *
   * NOT `cartItemRows[line - 1]`. A bundle folds a whole set of lines into a
   * single summary row (see snippets/cart-products.liquid), so the rendered rows
   * and the cart's lines stop being the same list the moment a bundle is in the
   * cart — and `/cart/add.js` puts new lines first, so adding a bundle shifts
   * every other item's line number up by the size of the box while leaving its
   * row where it was. The Liquid side writes the real number onto the row.
   *
   * @param {number} line - 1-based cart line number
   * @returns {HTMLElement | undefined}
   */
  #rowForLine(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.lineIndex === String(line));
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  #bindSaveForLaterButtons() {
    this.querySelectorAll('button.cart-items__save-later[data-variant-id][data-product-handle]').forEach((btn) => {
      btn.removeEventListener('click', this.#onSaveForLater);
      btn.addEventListener('click', this.#onSaveForLater);
    });
  }

  #rerenderSaveForLater() {
    this.querySelectorAll('save-for-later-component').forEach((el) => {
      /** @type {any} */ (el).syncPopulatedClass?.();
    });
  }

  /** @param {Event} event */
  #onSaveForLater = (event) => {
    const btn = /** @type {HTMLElement} */ (event.currentTarget);
    const variantId = btn.dataset.variantId;
    const productHandle = btn.dataset.productHandle;
    const quantity = parseInt(btn.dataset.quantity || '1', 10);
    const productTitle = btn.dataset.productTitle || '';
    const productImage = btn.dataset.productImage || '';
    const sellingPlanId = btn.dataset.sellingPlanId || '';

    if (!variantId || !productHandle) return;

    SaveForLaterStore.saveItem(variantId, productHandle, quantity, productTitle, productImage, sellingPlanId);

    // Find the cart row and remove it.
    //
    // The line number comes off the row, not from its position among the rendered
    // rows: with a bundle in the cart those two diverge (see #rowForLine), and
    // counting rows here would hand /cart/change.js a line number belonging to
    // some other item — saving one product for later by deleting another.
    const row = /** @type {HTMLTableRowElement | null} */ (btn.closest('.cart-items__table-row'));
    const line = Number.parseInt(row?.dataset.lineIndex ?? '', 10);
    if (Number.isFinite(line) && line > 0) {
      this.onLineItemRemove(line);
    }
  };

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rowForLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * The summary row standing in for one Super Subscriptions & Bundles offer+tier.
   * @param {string} rowId - `<group>~<bar>`, written by snippets/cart-products.liquid
   * @returns {HTMLElement | undefined}
   */
  #bundleRow(rowId) {
    return this.refs.cartItemRows.find((row) => row.dataset.bundleRowId === rowId);
  }

  /**
   * The bundles of one summary row, read back from the cart itself.
   *
   * The row carries the `_ssBundleKey`s it was rendered from, but not which cart
   * lines belong to which key — the per-line rows it replaces are, by design, never
   * rendered. Both steppers need that mapping (one to clone a bundle, the other to
   * drop one), and the cart is the only place it is not already stale.
   *
   * @param {HTMLElement} row
   * @returns {Promise<Map<string, Array<any>>>} `_ssBundleKey` → its cart lines
   */
  async #bundleUnits(row) {
    const group = row.dataset.bundleGroup;
    const bar = row.dataset.bundleBar || '0';

    const response = await fetch(`${Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart read failed: ${response.status}`);
    const cart = await response.json();

    /** @type {Map<string, Array<any>>} */
    const units = new Map();

    for (const line of cart.items ?? []) {
      const properties = line.properties ?? {};
      if (properties._ssBundleGroup !== group) continue;
      if ((properties._ssBundleBar ?? '0') !== bar) continue;
      const key = properties._ssBundleKey;
      if (key == null) continue;

      const lines = units.get(key);
      if (lines) lines.push(line);
      else units.set(key, [line]);
    }

    return units;
  }

  /**
   * Applies a cart mutation and re-renders from the server's own section HTML.
   *
   * Shared by all three bundle controls. None of them can take onLineItemRemove's
   * optimistic empty-cart-template shortcut: that path swaps in the empty state
   * before the server answers, which it can only do because it knows up front
   * whether the row it is removing is the last one. These change several lines at
   * once, and whether they were the whole cart is exactly what the response tells
   * us — so the freshly rendered section HTML is both simpler and the only thing
   * guaranteed to be right.
   *
   * @param {string} marker - performance marker label
   * @param {() => Promise<Response>} request - runs the mutation
   */
  async #applyBundleMutation(marker, request) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${marker}:user-action`);

    this.#disableCartItems();

    try {
      const response = await request();
      const parsedResponseText = await response.json();

      resetShimmer(this);

      if (parsedResponseText.errors || parsedResponseText.status) {
        console.error(parsedResponseText.errors ?? parsedResponseText.description);
        return;
      }

      const sectionHTML = parsedResponseText.sections?.[this.sectionId];
      if (!sectionHTML) return;

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });

      this.#updateCartQuantitySelectorButtonStates();
      this.#applySelectionState();
      this.#bindSaveForLaterButtons();
      this.#rerenderSaveForLater();
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /** Every cart section on the page, so the drawer and the cart page stay in step. */
  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate).join(',');
  }

  /**
   * Sets the given cart line keys to a quantity in one atomic /cart/update.js batch.
   * Unlike a native bundle's parent/child lines, these are independent Shopify cart
   * lines the app happens to tag with a shared id — there's no server-side
   * relationship tying them together, so changing them one at a time via
   * onLineItemRemove would strand whichever lines a failed request hadn't reached.
   * @param {string[]} lineKeys
   * @param {number} quantity
   * @param {string} marker
   */
  #setBundleLineQuantities(lineKeys, quantity, marker) {
    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of lineKeys) updates[key] = quantity;
    if (Object.keys(updates).length === 0) return Promise.resolve();

    const body = JSON.stringify({
      updates,
      sections: this.#sectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    const cartUpdateUrl = Theme?.routes?.cart_update_url || '/cart/update';

    return this.#applyBundleMutation(marker, () => fetch(`${cartUpdateUrl}.js`, fetchConfig('json', { body })));
  }

  /**
   * Removes every cart line of one bundle row — all of its bundles, not just one.
   * @param {string} rowId
   */
  onBundleGroupRemove(rowId) {
    const row = this.#bundleRow(rowId);
    if (!row) return;

    // The summary row stands in for its whole group — the real per-line cart
    // keys never get their own row, so they're only recoverable from the
    // data-bundle-member-keys attribute the Liquid side wrote on it.
    const memberKeys = (row.dataset.bundleMemberKeys || '').split(',').filter(Boolean);
    this.#setBundleLineQuantities(memberKeys, 0, 'bundle-group-remove');
  }

  /**
   * One more of this bundle.
   *
   * A bundle is a *set* of lines sharing an `_ssBundleKey`, and the app's discount
   * Function prices each such set on its own, so a second bundle is a second set
   * under a new key — not a doubled quantity on the existing one. (Doubling would
   * look right for a percentage-off bar and sell two boxes for the price of one on
   * a fixed-price bar.) The new set is cloned from a set already in the cart, so
   * every other property the app wrote — bar, block name, selling plan — carries
   * over untouched.
   *
   * @param {string} rowId
   */
  async onBundleUnitIncrease(rowId) {
    const row = this.#bundleRow(rowId);
    if (!row) return;

    let units;
    try {
      units = await this.#bundleUnits(row);
    } catch (error) {
      console.error(error);
      return;
    }

    const [templateKey, templateLines] = units.entries().next().value ?? [];
    if (!templateKey || !templateLines?.length) return;

    // Derived from a key already in the cart rather than from a clock or a random
    // number: it only has to be unique within this cart, and this is both unique
    // and reproducible if the same click is replayed.
    let suffix = units.size;
    let newKey = `${templateKey}+${suffix}`;
    while (units.has(newKey)) newKey = `${templateKey}+${(suffix += 1)}`;

    const items = templateLines.map((line) => {
      /** @type {Record<string, any>} */
      const item = {
        id: line.variant_id,
        quantity: line.quantity,
        properties: { ...line.properties, _ssBundleKey: newKey },
      };
      const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) item.selling_plan = sellingPlanId;
      return item;
    });

    const body = JSON.stringify({
      items,
      sections: this.#sectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    const cartAddUrl = Theme?.routes?.cart_add_url || '/cart/add';

    this.#applyBundleMutation('bundle-unit-increase', () =>
      fetch(cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`, fetchConfig('json', { body }))
    );
  }

  /**
   * One fewer of this bundle — the whole set of lines under one `_ssBundleKey`.
   * At the last one the control is a remove button (see `data-at-min` in
   * snippets/cart-products.liquid), so it takes the row out entirely.
   * @param {string} rowId
   */
  async onBundleUnitDecrease(rowId) {
    const row = this.#bundleRow(rowId);
    if (!row) return;

    if (parseInt(row.dataset.quantity ?? '1', 10) <= 1) {
      this.onBundleGroupRemove(rowId);
      return;
    }

    let units;
    try {
      units = await this.#bundleUnits(row);
    } catch (error) {
      console.error(error);
      return;
    }

    if (units.size <= 1) {
      this.onBundleGroupRemove(rowId);
      return;
    }

    const lastLines = Array.from(units.values()).pop() ?? [];
    this.#setBundleLineQuantities(
      lastLines.map((line) => line.key),
      0,
      'bundle-unit-decrease'
    );
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
        this.#applySelectionState();
        this.#bindSaveForLaterButtons();
        this.#rerenderSaveForLater();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Via the row rather than `quantitySelectors[line - 1]`: a bundle row carries
    // its own stepper markup instead of a quantity-selector-component, so that
    // array is a different length from the cart's lines (see #rowForLine).
    const quantitySelector = this.#rowForLine(line)?.querySelector('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#applySelectionState();
      this.#bindSaveForLaterButtons();
      this.#rerenderSaveForLater();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
